import { ApiResponse, ApiError } from '@/types';
import { PATHS } from '@/constants/paths';

class ApiClient {
  private apiBaseURL: string;
  private authBaseURL: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshFailed: boolean = false;

  constructor(baseURL: string = process.env.REACT_APP_API_URL || '/api') {
    const trimmedBaseURL = baseURL.replace(/\/$/, '');
    const normalizedApiBase = trimmedBaseURL.replace(/\/api\/?$/, '');
    this.apiBaseURL = normalizedApiBase || '/api';

    const explicitAuthBase = process.env.REACT_APP_AUTH_API_URL;
    if (explicitAuthBase) {
      this.authBaseURL = explicitAuthBase.replace(/\/$/, '');
    } else if (trimmedBaseURL) {
      this.authBaseURL = /\/api$/.test(trimmedBaseURL)
        ? trimmedBaseURL
        : `${normalizedApiBase}/api`;
    } else {
      this.authBaseURL = '/api';
    }
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  setTokens(accessToken: string | null, refreshToken: string | null) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
      this.refreshFailed = false;
    } else {
      localStorage.removeItem('access_token');
    }

    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
      this.refreshFailed = false;
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  setToken(token: string | null) {
    this.setTokens(token, this.refreshToken);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  private isAuthEndpoint(endpoint: string): boolean {
    return endpoint.startsWith('/auth/');
  }

  private requiresAuth(endpoint: string): boolean {
    const protectedAuthEndpoints = [
      '/auth/me/',
      '/auth/logout/',
      '/auth/password/change/',
    ];
    return protectedAuthEndpoints.some(protectedEndpoint =>
      endpoint.startsWith(protectedEndpoint)
    );
  }

  private resolveBaseURL(endpoint: string): string {
    return this.isAuthEndpoint(endpoint) ? this.authBaseURL : this.apiBaseURL;
  }

  private buildUrl(baseURL: string, endpoint: string): string {
    if (!baseURL) {
      return endpoint;
    }
    if (baseURL.endsWith('/') && endpoint.startsWith('/')) {
      return `${baseURL.slice(0, -1)}${endpoint}`;
    }
    if (!baseURL.endsWith('/') && !endpoint.startsWith('/')) {
      return `${baseURL}/${endpoint}`;
    }
    return `${baseURL}${endpoint}`;
  }

  private isPublicReadEndpoint(endpoint: string, method: string): boolean {
    if (method.toUpperCase() !== 'GET') return false;
    const pathOnly = endpoint.split('?')[0].replace(/\/$/, '') || '/';
    if (pathOnly === '/capsules/search') return true;
    if (/^\/capsules\/[^/]+\/$/.test(endpoint)) return true;
    if (/^\/capsules\/[^/]+\/comments\/$/.test(endpoint)) return true;
    return false;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const baseURL = this.resolveBaseURL(endpoint);
    const url = this.buildUrl(baseURL, endpoint);
    const isAuthEndpoint = this.isAuthEndpoint(endpoint);
    const needsAuth = this.requiresAuth(endpoint);
    const method = (options.method || 'GET').toString();
    const isPublicRead = this.isPublicReadEndpoint(endpoint, method);

    if (!isAuthEndpoint || needsAuth) {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      if (token) {
        this.accessToken = token;
      }
      if (refreshToken && !this.refreshToken) {
        this.refreshToken = refreshToken;
      }
      if (!refreshToken && this.refreshToken) {
        this.refreshToken = null;
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken && (!isAuthEndpoint || needsAuth)) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      let data;
      const contentType = response.headers.get('content-type');
      const text = await response.text();

      if (contentType && contentType.includes('application/json')) {
        try {
          data = text ? JSON.parse(text) : {};
        } catch (jsonError) {
          data = { message: 'Ошибка парсинга ответа сервера' };
        }
      } else {
        if (response.ok) {
          data = {};
        } else {
          data = {
            message: text || 'Произошла ошибка',
          };
        }
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || 'Произошла ошибка';
        const error: ApiError = {
          message: errorMessage,
          status: response.status,
          code: data.code,
          url,
          data,
        };
        if (
          response.status === 401 &&
          this.refreshToken &&
          (!isAuthEndpoint || needsAuth) &&
          !this.isRefreshing &&
          !this.refreshFailed
        ) {
          try {
            this.isRefreshing = true;
            this.refreshFailed = false;
            await this.refreshAccessToken();
            if (!this.refreshToken) {
              this.isRefreshing = false;
              this.refreshFailed = true;
              throw error;
            }
            this.isRefreshing = false;
            const retryResponse = await this.request<T>(endpoint, options);
            return retryResponse;
          } catch (refreshError) {
            this.isRefreshing = false;
            this.refreshFailed = true;
            this.setTokens(null, null);
            try {
              window.location.assign(PATHS.AUTH_REGISTER);
            } catch {
              // ignore navigation errors
            }
            throw error;
          }
        }

        if (
          response.status === 401 &&
          (!isAuthEndpoint || needsAuth) &&
          !isPublicRead
        ) {
          this.setTokens(null, null);
          try {
            window.location.assign(PATHS.AUTH_REGISTER);
          } catch {
            // ignore navigation errors
          }
        }

        throw error;
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      if (error instanceof TypeError) {
        const networkError: ApiError = {
          message: 'Ошибка сети. Проверьте подключение к интернету.',
          status: 0,
        };
        throw networkError;
      }
      if (error && typeof error === 'object' && 'status' in error) {
        throw error;
      }
      const unknownError: ApiError = {
        message:
          error instanceof Error
            ? error.message
            : 'Произошла неизвестная ошибка',
        status: 0,
      };
      throw unknownError;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const currentRefreshToken = this.refreshToken;

    const endpoint = '/auth/token/refresh/';
    const url = this.buildUrl(this.authBaseURL, endpoint);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: currentRefreshToken }),
    });

    if (!response.ok) {
      this.refreshToken = null;
      this.accessToken = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    if (data.access) {
      this.accessToken = data.access;
      if (data.refresh) {
        this.refreshToken = data.refresh;
        localStorage.setItem('refresh_token', data.refresh);
      }
      localStorage.setItem('access_token', data.access);
    }
  }

  async upload<T>(
    endpoint: string,
    formData: FormData
  ): Promise<ApiResponse<T>> {
    const baseURL = this.resolveBaseURL(endpoint);
    const url = this.buildUrl(baseURL, endpoint);

    const headers: HeadersInit = {};
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data: any = {};
    const contentType = response.headers.get('content-type');
    const text = await response.text();

    if (contentType && contentType.includes('application/json') && text) {
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        data = {};
      }
    }

    if (!response.ok) {
      const errorMessage =
        data.detail || data.message || 'Ошибка загрузки файла';
      const error: ApiError = {
        message: errorMessage,
        status: response.status,
        code: data.code,
        url,
        data,
      };

      throw error;
    }

    return {
      success: true,
      data: data as T,
    };
  }
}

export const apiClient = new ApiClient();
